import * as React from 'react';
import { Star, Clock, ChevronDown, Search, X, ArrowRight, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { ProjectWithMetadata, ProjectFilters } from '@/types/project-selector';
import { useProjectSelector } from '@/hooks/use-project-selector';
import { ProjectSearchModal } from './ProjectSearchModal';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from '@/components/ui/scroll-area';
import type { Project } from '@/types';

interface ProjectSelectorProps {
  projects: Project[] | ProjectWithMetadata[];
  value?: string;
  onChange: (projectId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ProjectSelector({
  projects,
  value,
  onChange,
  disabled,
  placeholder = 'Select project...',
}: ProjectSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const {
    enrichedProjects,
    favouriteProjects,
    recentProjects,
    toggleFavourite,
    recordProjectAccess,
    isFavourite,
  } = useProjectSelector(projects);

  const selectedProject = enrichedProjects.find((p) => p.id === value);

  // Handle project selection
  const handleSelectProject = React.useCallback(
    (projectId: string) => {
      // Check if project is archived/inactive
      const project = enrichedProjects.find((p) => p.id === projectId);
      // Handle both Project (active: boolean) and ProjectWithMetadata (status: string) types
      const isArchived = 'status' in project! && project.status === 'archived';
      const isInactive = 'active' in project! && project.active === false;
      if (isArchived || isInactive) {
        alert('Cannot select archived/inactive project. Please choose an active project.');
        return;
      }

      onChange(projectId);
      recordProjectAccess(projectId);
      setDropdownOpen(false);
    },
    [enrichedProjects, onChange, recordProjectAccess]
  );

  // Handle modal open
  const handleOpenModal = React.useCallback(() => {
    setModalOpen(true);
    setDropdownOpen(false);
  }, []);

  // Limit favourites and recent in dropdown
  const dropdownFavourites = favouriteProjects.slice(0, 5);
  const dropdownRecent = recentProjects.slice(0, 5);

  return (
    <>
      <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              value={
                selectedProject
                  ? `${selectedProject.name} (${selectedProject.code})`
                  : ''
              }
              placeholder={placeholder}
              readOnly
              disabled={disabled}
              className={cn(
                'cursor-pointer pr-10',
                selectedProject?.isFavourite && 'pl-8'
              )}
              onClick={() => !disabled && setDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  !disabled && setDropdownOpen(true);
                }
                // Ctrl/Cmd + K to open modal
                if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                  e.preventDefault();
                  !disabled && handleOpenModal();
                }
              }}
              aria-label="Select project"
              aria-expanded={dropdownOpen}
              role="combobox"
            />
            {selectedProject?.isFavourite && (
              <Star className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
            )}
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '300px' }}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <ScrollArea className="max-h-[400px]">
            <ScrollAreaViewport className="max-h-[400px] p-2">
              {/* Favourites Section */}
              <div className="mb-3">
                <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5" />
                  FAVOURITES
                </div>
                {dropdownFavourites.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground italic">
                    No favourites yet. Star projects to add them here.
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {dropdownFavourites.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleSelectProject(project.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                          value === project.id && 'bg-accent'
                        )}
                      >
                        <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 shrink-0" />
                        <span className="flex-1 truncate">
                          {project.name}{' '}
                          <span className="text-muted-foreground">
                            ({project.code})
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Recently Used Section */}
              <div className="mb-3">
                <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  RECENTLY USED
                </div>
                {dropdownRecent.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground italic">
                    No recently used projects
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {dropdownRecent.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleSelectProject(project.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                          value === project.id && 'bg-accent'
                        )}
                      >
                        <span className="flex-1 truncate">
                          {project.name}{' '}
                          <span className="text-muted-foreground">
                            ({project.code})
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical">
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
          </ScrollArea>

          <Separator className="my-0" />
          <div className="p-2">
            <Button
              variant="ghost"
              className="w-full justify-center text-sm"
              onClick={handleOpenModal}
            >
              Show More...
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Full Search Modal */}
      <ProjectSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        projects={enrichedProjects.map(p => ({
          ...p,
          // Map Project's active boolean to ProjectWithMetadata's status string
          status: 'status' in p ? p.status : (p.active ? 'active' : 'archived'),
          // Ensure description exists (optional in Project)
          description: 'description' in p ? p.description : undefined,
        })) as ProjectWithMetadata[]}
        value={value}
        onSelect={handleSelectProject}
        onToggleFavourite={toggleFavourite}
        isFavourite={isFavourite}
      />
    </>
  );
}
